/**
 * useMint Hook - Mint Status Tracking
 *
 * Tracks the status of the mint side of CCTP transfers.
 * The actual minting is done by the backend relayer - this hook just polls for status.
 *
 * Flow:
 * 1. Frontend burns USDC (useBurn hook)
 * 2. Frontend submits burn tx hash to backend (initiateTransfer)
 * 3. Backend monitors for attestation and completes mint
 * 4. This hook polls backend for status updates
 */

import { useState, useCallback, useRef } from 'react'
import { bridgeApi } from '@/services/api'
import { getDisplayError } from '@/utils/errors'
import type { Transfer, TransferStatus } from '@/types'

// Polling configuration. Poll every 5s (was 15s) so the UI reflects completion within ~5s of the backend
// recording mint_success — the 15s interval made users stare at a spinner for up to 15s AFTER their funds
// had already landed. The status endpoint (/bridge/hybrid/status) is an unauthenticated cheap SQLite read
// with no rate limit, so a 5s cadence is safe. MAX_POLL_COUNT bumped to keep the total window ~10 min
// (5s × 120); MAX_CONSECUTIVE_FAILURES raised to preserve a ~30s transient-error tolerance at the faster cadence.
const POLL_INTERVAL_MS = 5000 // 5 seconds between polls
const MAX_POLL_COUNT = 120 // ~10 minutes max polling time (5s × 120)
const MAX_CONSECUTIVE_FAILURES = 6 // Stop after 6 consecutive API failures (~30s at 5s cadence)

// Terminal states - stop polling when reached
const TERMINAL_STATES: TransferStatus[] = ['mint_success', 'recovered', 'failed']

interface UseMintReturn {
  mintStatus: TransferStatus | null
  mintTxHash: string | null
  mintError: string | null
  isPolling: boolean
  transfer: Transfer | null
  pollMintStatus: (burnTxHash: string) => void
  stopPolling: () => void
  reset: () => void
}

export function useMint(): UseMintReturn {
  const [mintStatus, setMintStatus] = useState<TransferStatus | null>(null)
  const [mintTxHash, setMintTxHash] = useState<string | null>(null)
  const [mintError, setMintError] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [transfer, setTransfer] = useState<Transfer | null>(null)

  // Refs to track polling state across async calls
  const pollCountRef = useRef(0)
  const consecutiveFailuresRef = useRef(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isPollingRef = useRef(false)

  /**
   * Stop any active polling
   */
  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    isPollingRef.current = false
    setIsPolling(false)
  }, [])

  /**
   * Reset all state and stop polling
   */
  const reset = useCallback(() => {
    stopPolling()
    setMintStatus(null)
    setMintTxHash(null)
    setMintError(null)
    setTransfer(null)
    pollCountRef.current = 0
    consecutiveFailuresRef.current = 0
  }, [stopPolling])

  /**
   * Single poll iteration
   */
  const executePoll = useCallback(async (burnTxHash: string): Promise<boolean> => {
    try {
      const response = await bridgeApi.getTransferStatus(burnTxHash)

      if (response.success && response.data) {
        // Reset failure counter on success
        consecutiveFailuresRef.current = 0

        // Update state with response data
        const data = response.data
        setTransfer(data)
        setMintStatus(data.status)

        // Capture mint transaction hash when available
        if (data.mintTxHash) {
          setMintTxHash(data.mintTxHash)
        }

        console.log(`📊 Transfer status: ${data.status}`, {
          burnTxHash: data.burnTxHash?.slice(0, 10) + '...',
          mintTxHash: data.mintTxHash ? data.mintTxHash.slice(0, 10) + '...' : 'pending',
          pollCount: pollCountRef.current,
        })

        // Check if we've reached a terminal state
        if (TERMINAL_STATES.includes(data.status)) {
          if (data.status === 'mint_success') {
            console.log('✅ Mint completed successfully!')
          } else if (data.status === 'recovered') {
            console.log('🔄 Transfer recovered successfully!')
          } else if (data.status === 'failed') {
            console.log('❌ Transfer failed')
            setMintError(getDisplayError(data.statusMessage || 'Transfer failed'))
          }
          return true // Terminal state reached
        }

        return false // Continue polling
      } else {
        // API returned error response
        consecutiveFailuresRef.current++
        const errorMsg = response.error?.message || 'Failed to get transfer status'
        console.warn(`⚠️ Poll failed (${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES}):`, errorMsg)

        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setMintError(`Failed to get status after ${MAX_CONSECUTIVE_FAILURES} attempts: ${getDisplayError(errorMsg)}`)
          return true // Stop polling due to repeated failures
        }

        return false // Continue polling
      }
    } catch (e) {
      // Network or unexpected error
      consecutiveFailuresRef.current++
      const errorMsg = e instanceof Error ? e.message : 'Unknown error'
      console.error(`❌ Poll error (${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES}):`, errorMsg)

      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        setMintError(`Polling failed after ${MAX_CONSECUTIVE_FAILURES} attempts: ${getDisplayError(errorMsg)}`)
        return true // Stop polling due to repeated failures
      }

      return false // Continue polling
    }
  }, [])

  /**
   * Polling loop
   */
  const pollLoop = useCallback(async (burnTxHash: string) => {
    // Check if we should stop
    if (!isPollingRef.current) {
      return
    }

    // Check max poll count
    if (pollCountRef.current >= MAX_POLL_COUNT) {
      console.warn('⏰ Max poll count reached, stopping')
      setMintError('Transfer status polling timed out. The transfer may still complete - check transaction explorer.')
      stopPolling()
      return
    }

    pollCountRef.current++

    // Execute poll
    const shouldStop = await executePoll(burnTxHash)

    if (shouldStop) {
      stopPolling()
      return
    }

    // Schedule next poll if still polling
    if (isPollingRef.current) {
      timeoutRef.current = setTimeout(() => {
        pollLoop(burnTxHash)
      }, POLL_INTERVAL_MS)
    }
  }, [executePoll, stopPolling])

  /**
   * Start polling for mint status
   */
  const pollMintStatus = useCallback((burnTxHash: string) => {
    // Don't start if already polling
    if (isPollingRef.current) {
      console.warn('Already polling, ignoring duplicate call')
      return
    }

    // Validate input
    if (!burnTxHash) {
      setMintError('No burn transaction hash provided')
      return
    }

    console.log('🔍 Starting mint status polling for:', burnTxHash.slice(0, 10) + '...')

    // Reset counters
    pollCountRef.current = 0
    consecutiveFailuresRef.current = 0
    setMintError(null)

    // Start polling
    isPollingRef.current = true
    setIsPolling(true)

    // Initial poll immediately
    pollLoop(burnTxHash)
  }, [pollLoop])

  return {
    mintStatus,
    mintTxHash,
    mintError,
    isPolling,
    transfer,
    pollMintStatus,
    stopPolling,
    reset,
  }
}
